/**
 * Xuất debug log — Prompt.md §25.
 *
 * ⚠️ PRIVACY GUARD (§7): log CHỈ chứa **số** (toạ độ landmark đã chuẩn hoá + metrics).
 * TUYỆT ĐỐI không thêm frame ảnh, không `canvas.toDataURL()`, không snapshot video — kể cả
 * "chỉ để debug cho dễ". Một khi có ảnh trong file export, cam kết "hình ảnh không rời thiết bị"
 * của app không còn đúng, và người thêm sẽ không phải là người phát hiện.
 */

import type { CameraPlacement } from "@studystraight/shared-types";

import type { DebugSample } from "./debugMetrics.js";

export interface DebugLogMeta {
  placement: CameraPlacement;
  engine: string;
  delegate: "GPU" | "CPU";
  targetFps: number;
  actualFps: number;
  inferenceP50Ms: number;
  inferenceP95Ms: number;
  droppedFrames: number;
  /** ms — chỉ để đặt tên file và biết log của lần chạy nào. */
  exportedAt: number;
  userAgent: string;
}

export interface DebugLog extends DebugLogMeta {
  schemaVersion: 1;
  samples: readonly DebugSample[];
}

export function buildDebugLog(meta: DebugLogMeta, samples: readonly DebugSample[]): DebugLog {
  return { schemaVersion: 1, ...meta, samples };
}

function fileName(exportedAt: number): string {
  const iso = new Date(exportedAt).toISOString().replace(/[:.]/g, "-");
  return `studystraight-debug-${iso}.json`;
}

/**
 * Ưu tiên `navigator.share` (trên điện thoại là cách duy nhất chuyển file ra máy tính mà không
 * cắm cáp), fallback download. Cả hai đều là I/O nội bộ thiết bị — không upload.
 */
export async function exportDebugLog(log: DebugLog): Promise<"shared" | "downloaded"> {
  const json = JSON.stringify(log);
  const name = fileName(log.exportedAt);
  const file = new File([json], name, { type: "application/json" });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "StudyStraight debug log" });
      return "shared";
    } catch {
      // Người dùng huỷ share sheet — rơi xuống download, không coi là lỗi.
    }
  }

  const url = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
  return "downloaded";
}
